import React from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';
import { mainSiteUrl } from '@/mocks/categories';

interface SeoPageWrapperProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  breadcrumbLabel: string;
  pageTitle: string;
  subtitle: string;
  readTime: string;
  category: string;
  children: React.ReactNode;
}

function buildArticleSchema(
  headline: string,
  description: string,
  canonicalUrl: string,
  datePublished: string,
  dateModified: string,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Hub',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Hub',
    },
    datePublished,
    dateModified,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://sugargoosheet.baby${canonicalUrl}`,
    },
  };
}

export default function SeoPageWrapper({
  title,
  description,
  keywords,
  canonicalUrl,
  breadcrumbLabel,
  pageTitle,
  subtitle,
  readTime,
  category,
  children,
}: SeoPageWrapperProps) {
  const articleSchema = buildArticleSchema(
    title,
    description,
    canonicalUrl,
    '2026-08-01',
    '2026-08-09',
  );

  return (
    <main>
      <JsonLd data={articleSchema} />
      <Navbar />
      <section className="pt-28 md:pt-32 pb-10 md:pb-12 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="Breadcrumb">
            <a href="/" className="hover:text-primary-500 transition-colors duration-200">Home</a>
            <span>/</span>
            <span className="text-foreground-700">{breadcrumbLabel}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
            {pageTitle}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed">
            {subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Updated August 2026
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line"></i>
              {category}
            </span>
          </div>
        </div>
      </section>

      <article className="py-10 md:py-14 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="space-y-8 md:space-y-10">
            {children}
          </div>

          <div className="mt-12 p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-200/60 text-center">
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
              Ready to Start Shopping Smarter?
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
              Explore the complete Sugargoo Spreadsheet with organized product categories, verified links, and community-curated recommendations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/"
                className="whitespace-nowrap w-full sm:w-auto px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer"
              >
                Back to Homepage
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap w-full sm:w-auto px-6 py-2.5 rounded-full border-2 border-background-200 text-foreground-800 text-sm font-semibold hover:border-primary-300 transition-all duration-200 cursor-pointer"
              >
                Visit Sugargoo Spreadsheet
              </a>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}